import React, { useCallback, useMemo } from "react";
import styled, { useTheme } from "styled-components";

import {
  Box,
  Flex,
  Text,
  Button,
  ImageView,
  ActivityIndicator,
  useBoolean,
  pluralize,
  useThemeColor,
  utils,
} from "@thenounproject/lingo-core";
import { CanvasComponentProps } from "./sharedProps";
import { ItemPolling } from "../GalleryAssetContent/PollingWrappers";
import { getVersionedItemId } from "@src/helpers/items";
import useModals, { ModalTypes } from "@actions/useModals";
import useReplaceAssetFile from "@actions/assets/useReplaceAssetFile";
import useCreateItemAsset from "@actions/items/useCreateItemAsset";
import { getCanvasLayoutProperties } from "./utils";
import { SelectionWrapper } from "./SelectionWrapper";
import { CanvasItemWrapper } from "./CanvasItemWrapper";
import getAssetBackground from "@src/helpers/getAssetBackground";
import useCreateAssetsFromSource, {
  ExternalSource,
} from "@actions/items/useCreateAssetsFromSource";
import useReplaceAssetFileFromSource from "@actions/assets/useReplaceAssetFileFromSource";

const { getTextColor, getColorBasedOnLightness } = utils;

type Props = CanvasComponentProps & {
  containerWidth: number;
};

const Wrapper = styled(Flex).attrs<{ backgroundColor: string }>(props => ({
  borderRadius: "default",
  width: "100%",
  background: props.backgroundColor,
  alignItems: "center",
}))<{ backgroundColor: string }>`
  ${utils.showOnHoverCSS("show-on-hover")}
`;

const ButtonContainer = styled(Flex).attrs({
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
})`
  aspect-ratio: 3/2;
`;

const getFlexDirection = (alignment: string, size: number) => {
  if (!alignment) return "row";
  if (size === 1) return "column";
  if (alignment === "top") return "column";
  if (alignment === "leading") return "row";
  return "row-reverse";
};

const getCardPadding = (isSquare: boolean, size: number, background: string) => {
  if (background === "clear") return "none";
  if (!isSquare) return "m";
  return size > 2 ? "s" : "m";
};

const CanvasGallery: React.FC<Props> = ({
  item,
  isSelected,
  canEdit,
  onClick,
  onDoubleClick,
  onOverflowSelected,
  onDragEnd,
  onDragStart,
  draggable,
  index,
  containerWidth,
  ...rest
}) => {
  const versionedId = getVersionedItemId(item);
  const { outerWidth, size, isSquare, innerThumbnailWidth } = getCanvasLayoutProperties({
    item,
    containerWidth,
  });
  const [hovered, setHovered, setNotHovered] = useBoolean(false);
  // Display properties
  const { imageAlignment, cardBackgroundColor: _cardBackgroundColor } = item.displayProperties;
  const cardBackgroundColor = useThemeColor(_cardBackgroundColor, "white");
  const flexDirection = getFlexDirection(imageAlignment, size);
  const justifyContent = imageAlignment === "trailing" ? "space-between" : "flex-start";
  const padding = getCardPadding(isSquare, size, cardBackgroundColor);
  const primaryTextColor = getTextColor(cardBackgroundColor);
  const secondaryTextColor = getColorBasedOnLightness(cardBackgroundColor, "grayDarkest", "gray");
  const thumbnailWidth = imageAlignment === "top" ? "100%" : innerThumbnailWidth;
  const theme = useTheme();

  const backgroundImage = useMemo(() => {
    if (item.asset?.meta?.assetProcessing === "complete") {
      return item.asset?.thumbnails["1232"];
    }
    return null;
  }, [item.asset?.thumbnails, item.asset?.meta?.assetProcessing]);

  const backgroundProps = useMemo(() => {
    return getAssetBackground(item.asset, theme);
  }, [item.asset, theme]);

  // Actions
  const { showModal } = useModals();
  const [replaceAssetFile] = useReplaceAssetFile();
  const [createItemAsset] = useCreateItemAsset();
  const [replaceAssetFileFromSource] = useReplaceAssetFileFromSource();
  const [createAssetFromSource] = useCreateAssetsFromSource();

  const onFileSelected = useCallback(
    (files: File[]) => {
      const file = files[0];
      return item.asset
        ? replaceAssetFile({ inspectable: { item, asset: item.asset }, file })
        : createItemAsset({ item, file });
    },
    [item, replaceAssetFile, createItemAsset]
  );
  const onSourceSelected = useCallback(
    (sources: ExternalSource[]) => {
      return item.asset
        ? replaceAssetFileFromSource({
            inspectable: { item, asset: item.asset },
            source: sources[0],
          })
        : createAssetFromSource({
            sources,
            insertPosition: { kitId: item.kitId, spaceId: item.spaceId },
            item,
          });
    },
    [item, replaceAssetFileFromSource, createAssetFromSource]
  );

  const replaceFileAsset = useCallback(() => {
    showModal(ModalTypes.PICK_FILE, {
      onUploadFiles: onFileSelected,
      onImportFiles: onSourceSelected,
      prompt: "Choose image",
      inspectable: { item, asset: item.asset },
    });
  }, [item, onSourceSelected, onFileSelected, showModal]);

  const handleOverflowClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOverflowSelected?.(item);
    },
    [item, onOverflowSelected]
  );

  const _onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onClick(item, e);
    },
    [onClick, item]
  );

  const _onDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onDoubleClick(item, e);
    },
    [onDoubleClick, item]
  );

  const routeToItem = useCallback(() => {
    onDoubleClick(item, null);
  }, [item, onDoubleClick]);

  const renderImageSection = useCallback(() => {
    if (item.asset?.meta?.assetProcessing === "processing") {
      return (
        <ButtonContainer>
          <ActivityIndicator size="default" center color="neutralLoader" />
        </ButtonContainer>
      );
    }
    if (!item.asset && canEdit && hovered) {
      return (
        <ButtonContainer>
          <Button
            buttonStyle="tertiary"
            text="Add image"
            icon="action.add"
            size="small"
            onClick={e => {
              e.stopPropagation();
              replaceFileAsset();
            }}
          />
        </ButtonContainer>
      );
    }

    return (
      <ImageView
        src={backgroundImage}
        icon="content.grid"
        iconSize="56"
        iconFill="grayDark"
        aspectRatio={66.6666}
        cover
        {...("background" in backgroundProps || "backgroundImage" in backgroundProps
          ? backgroundProps
          : { background: "grayLighter" })}
      />
    );
  }, [item, canEdit, hovered, backgroundImage, replaceFileAsset, backgroundProps]);

  return (
    <CanvasItemWrapper
      data-full-width={outerWidth === "100%" || size === 1}
      outerWidth={outerWidth}
      size={size}
      index={index}
      itemId={item.id}
      {...rest}>
      <Box
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        id={item.id}
        data-itemid={versionedId}
        data-dragid={versionedId}
        data-shortid={item.shortId}
        position="relative"
        onClick={_onClick}
        onDoubleClick={_onDoubleClick}>
        <ItemPolling item={item} />
        <SelectionWrapper selected={isSelected}>
          <Wrapper
            data-selected={isSelected}
            onDoubleClick={routeToItem}
            data-shortid={item.shortId}
            backgroundColor={cardBackgroundColor}
            flexDirection={flexDirection}
            justifyContent={justifyContent}
            p={padding}>
            <Box
              width={thumbnailWidth}
              borderRadius="2px"
              overflow="hidden"
              onMouseEnter={setHovered}
              {...("background" in backgroundProps || "backgroundImage" in backgroundProps
                ? backgroundProps
                : { background: "grayLighter" })}
              onMouseLeave={setNotHovered}>
              {renderImageSection()}
            </Box>
            <Flex width="100%" flex="1">
              <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="flex-start"
                px={!isSquare ? "l" : "none"}>
                <Flex flexDirection="column" pt={isSquare ? "s" : "none"}>
                  <Text font="gallery.h1" color={primaryTextColor}>
                    {item.data?.viewName || item.data?.name}
                  </Text>
                  {item.data.itemProcessing === "pending" ? (
                    <Flex alignItems="center">
                      <ActivityIndicator size="extraSmall" color="grayDark" mr="4" />
                      <Text font="ui.small" color={secondaryTextColor}>
                        Loading...
                      </Text>
                    </Flex>
                  ) : (
                    <Text font="ui.small" color={secondaryTextColor}>
                      {item.data?.assets} {pluralize("asset", item.data?.assets)}
                    </Text>
                  )}
                </Flex>
                <Flex mt="m" alignItems="center" gap="8px">
                  <Button
                    buttonStyle="secondary"
                    size="small"
                    onClick={routeToItem}
                    icon="navigation.chevron-right"
                    text="View"
                  />
                  <Button
                    className="show-on-hover"
                    dataPopupSource={`overflow-menu-${item.id}`}
                    px="s"
                    icon="navigation.overflow"
                    buttonStyle="tertiary"
                    onClick={handleOverflowClick}
                    themeOverrides={{
                      primaryColor: "grayDarkest",
                      primaryColorDark: "black",
                    }}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Wrapper>
        </SelectionWrapper>
      </Box>
    </CanvasItemWrapper>
  );
};

export default CanvasGallery;
